import { useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Check, Trash2, UserMinus } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataTable } from '@/components/DataTable'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Modal } from '@/components/Modal'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuth } from '@/auth/useAuth'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import { formatDate } from '@/lib/locale'
import {
  useMembers,
  useMemberAction,
  useRoles,
  useSetMemberRole,
} from '@/features/team/queries'
import type { TeamMember } from '@/features/team/types'

const errMsg = (err: unknown, fallback = 'Action failed.') => {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

export function MembersTab() {
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const canManage = hasPermission('team.manage')

  const membersQ = useMembers()
  const rolesQ = useRoles()
  const setRoleM = useSetMemberRole()
  const actionM = useMemberAction()

  const [confirmRemove, setConfirmRemove] = useState<TeamMember | null>(null)

  const roles = rolesQ.data?.roles ?? []

  const onSetRole = async (id: number, roleId: number) => {
    try {
      await setRoleM.mutateAsync({ id, roleId })
      toast({ title: 'Role updated.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Failed to update role', description: errMsg(err) })
    }
  }

  const onAction = async (m: TeamMember, kind: 'activate' | 'deactivate' | 'remove') => {
    try {
      await actionM.mutateAsync({ id: m.id, kind })
      if (kind === 'remove') setConfirmRemove(null)
      toast({
        title:
          kind === 'activate' ? 'Member reactivated.'
          : kind === 'deactivate' ? 'Member deactivated.'
          : 'Member removed.',
      })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Action failed', description: errMsg(err) })
    }
  }

  const columns: ColumnDef<TeamMember, unknown>[] = [
    {
      header: 'Member',
      accessorKey: 'email',
      cell: ({ row }) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{row.original.name ?? row.original.email}</p>
          <p className="truncate text-xs text-muted-foreground">{row.original.email}</p>
        </div>
      ),
    },
    {
      header: 'Role',
      accessorKey: 'role_name',
      cell: ({ row }) => {
        const m = row.original
        if (canManage && m.role_id != null) {
          return (
            <Select
              value={String(m.role_id)}
              onValueChange={(v) => void onSetRole(m.id, Number(v))}
              disabled={setRoleM.isPending}
            >
              <SelectTrigger className="h-8 w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {roles.map((r) => (
                  <SelectItem key={r.id} value={String(r.id)}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )
        }
        return <Badge variant="secondary">{m.role_name ?? '-'}</Badge>
      },
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: ({ row }) => (
        <Badge variant={row.original.status === 'active' ? 'success' : 'secondary'}>
          {row.original.status ?? '-'}
        </Badge>
      ),
    },
    {
      header: 'Last login',
      accessorKey: 'last_login_at',
      cell: ({ row }) =>
        row.original.last_login_at ? (
          <span className="text-xs text-muted-foreground">{formatDate(row.original.last_login_at)}</span>
        ) : (
          <span className="text-xs text-muted-foreground">-</span>
        ),
    },
    {
      header: '',
      id: 'actions',
      cell: ({ row }) => {
        const m = row.original
        if (!canManage) return null
        return (
          <div className="flex flex-wrap justify-end gap-1">
            {m.status === 'active' ? (
              <Button size="sm" variant="secondary" onClick={() => onAction(m, 'deactivate')}>
                <UserMinus className="size-4" /> Deactivate
              </Button>
            ) : (
              <Button size="sm" variant="secondary" onClick={() => onAction(m, 'activate')}>
                <Check className="size-4" /> Reactivate
              </Button>
            )}
            <Button size="sm" variant="danger" onClick={() => setConfirmRemove(m)}>
              <Trash2 className="size-4" /> Remove
            </Button>
          </div>
        )
      },
    },
  ]

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="p-0">
          {membersQ.isError ? (
            <Alert variant="destructive" className="m-3">
              <AlertDescription>Failed to load members: {errMsg(membersQ.error)}</AlertDescription>
            </Alert>
          ) : (
            <DataTable
              columns={columns}
              rows={membersQ.data?.members ?? []}
              loading={membersQ.isLoading}
              empty="No members yet. Invite someone to get started."
            />
          )}
        </CardContent>
      </Card>

      <Modal.Confirm
        open={confirmRemove !== null}
        onOpenChange={(open) => !open && setConfirmRemove(null)}
        title="Remove member?"
        body={
          confirmRemove ? (
            <p>
              Remove <strong>{confirmRemove.email}</strong> from the tenant? They will lose access immediately.
            </p>
          ) : null
        }
        onConfirm={() => {
          if (confirmRemove) void onAction(confirmRemove, 'remove')
        }}
        onCancel={() => setConfirmRemove(null)}
        busy={actionM.isPending}
      />
    </div>
  )
}
